// src/hooks/useSubscription.js
// Single source of truth for subscription state and quota across all components.

import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

// ── Pricing — edit here to change prices everywhere ──────────────────────────
export const PRICING = {
  monthly: {
    label:          'MONTHLY',
    price:          3000,
    priceLabel:     '₦3,000/mo',
    scansPerMonth:  4,
    styleUnlimited: true,
    description:    'Billed every month. Cancel anytime.',
    badge:          null,
  },
  yearly: {
    label:          'YEARLY',
    price:          20000,
    priceLabel:     '₦20,000/yr',
    scansPerMonth:  6,
    styleUnlimited: true,
    description:    'Billed once per year. Best value.',
    badge:          'SAVE 44%',
  },
};

export const FREE_LIMITS = {
  scans:       1,
  styleTryOns: 1,
};

export const SUPER_ADMINS = [
  "FFXDokpj5IXR3SFSvJyDu70LRp73",
  "GbjKMdeVEmTgMefcU1cEHsEOstn1",
];

export function isSameCalendarMonth(ts) {
  if (!ts) return false;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

export function formatNaira(amount) {
  if (!amount && amount !== 0) return '₦0';
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' })
    .format(amount).replace('NGN', '₦');
}

export function useSubscription() {
  const [uid,      setUid]      = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUid(u?.uid || null);
      if (!u) { setUserData(null); setLoading(false); }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      setUserData(snap.exists() ? snap.data() : null);
      setLoading(false);
    });
    return () => unsub();
  }, [uid]);

  const isSuperAdmin = SUPER_ADMINS.includes(uid);
  const tier         = userData?.subscriptionTier || 'free';

  const isSubscriptionActive = (() => {
    if (isSuperAdmin) return true;
    if (tier === 'free') return false;
    const end = userData?.subscriptionEndDate;
    if (!end) return false;
    return (end.toDate ? end.toDate() : new Date(end)) > new Date();
  })();

  const monthlyScansUsed = isSameCalendarMonth(userData?.monthlyScansResetDate)
    ? (userData?.monthlyScansUsed || 0) : 0;

  const lifetimeScansUsed  = userData?.lifetimeScansUsed  || 0;
  const lifetimeStylesUsed = userData?.lifetimeStylesUsed || 0;

  const canScan = (() => {
    if (isSuperAdmin) return { allowed: true };
    if (!isSubscriptionActive) {
      if (lifetimeScansUsed >= FREE_LIMITS.scans)
        return { allowed: false, reason: 'free_exhausted', message: 'Your 1 free scan has been used.' };
      return { allowed: true };
    }
    const cap = PRICING[tier]?.scansPerMonth || 0;
    if (monthlyScansUsed >= cap)
      return { allowed: false, reason: 'monthly_cap', message: `Monthly limit of ${cap} scans reached.` };
    return { allowed: true };
  })();

  const canStyleTryOn = (() => {
    if (isSuperAdmin) return { allowed: true };
    if (!isSubscriptionActive) {
      if (lifetimeStylesUsed >= FREE_LIMITS.styleTryOns)
        return { allowed: false, reason: 'style_exhausted', message: 'Your 1 free style try-on has been used.' };
      return { allowed: true };
    }
    return { allowed: true };
  })();

  return {
    loading, uid, userData, tier,
    isSuperAdmin, isSubscriptionActive,
    monthlyScansUsed, lifetimeScansUsed, lifetimeStylesUsed,
    canScan, canStyleTryOn,
    PRICING, FREE_LIMITS, formatNaira,
    bonusScans:        userData?.bonusScans        || 0,
  };
}
